import { useCallback, useMemo } from "react"

import { useAgentsList } from "@/agents/catalog"

import type { LaunchRunV1Request } from "@/types/api/runs-api-v1"

import type { UseLaunchConfigParams } from "./launchConfigTypes"
import { buildLaunchDisplayName, isMultiRunTemplate } from "./launchDisplayName"
import { getSelectedMetricIds } from "./selectedMetricIds"

export const useLaunchConfig = ({
  templateData,
  selectedTestCaseIdsByTestSet,
  testSetRecords,
  metricRecords,
  personaRecords,
  username,
  validationErrors,
  isLoading,
  runDisplayName,
  tags,
  customerMetadata,
}: UseLaunchConfigParams) => {
  const isLaunchDisabled = validationErrors.length > 0 || isLoading

  const isMultiRun = useMemo(
    () =>
      isMultiRunTemplate({
        agentIds: templateData.agentIds,
        personaIds: templateData.personaIds,
        testSetIds: templateData.testSetIds,
      }),
    [templateData.agentIds, templateData.personaIds, templateData.testSetIds],
  )

  // Fetch agents to get display names for auto-suffix (only when suffix generation is possible)
  const shouldFetchAgents = Boolean(runDisplayName?.trim() && isMultiRun)
  const { data: agents } = useAgentsList({ enabled: shouldFetchAgents })

  const buildDisplayName = useCallback(
    (
      agentId: string,
      personaId: string,
      testSetId?: string,
    ): string | undefined => {
      return buildLaunchDisplayName({
        runDisplayName,
        isMultiRun,
        agents,
        personaRecords,
        testSetRecords,
        testSetIds: templateData.testSetIds,
        agentId,
        personaId,
        testSetId,
      })
    },
    [
      runDisplayName,
      isMultiRun,
      agents,
      personaRecords,
      testSetRecords,
      templateData.testSetIds,
    ],
  )

  const generateLaunchRunRequestForAgent = useCallback(
    (
      agentId: string,
      personaId: string,
      testSetId: string,
      sharedSeed?: number,
      mutationIds?: string[],
    ): LaunchRunV1Request | null => {
      const selectedTestSet = testSetRecords.find(
        (record) => record.id === testSetId,
      )

      if (!selectedTestSet) {
        return null
      }

      const metricIds = getSelectedMetricIds(
        templateData.enabledMetrics,
        metricRecords,
      )
      if (!metricIds) {
        return null
      }

      const isCrossSetSelection =
        templateData.testSetIds.length > 1 &&
        Boolean(templateData.selectedTestCaseIds?.length)
      const selectedCaseIds = isCrossSetSelection
        ? selectedTestCaseIdsByTestSet?.[testSetId]
        : templateData.selectedTestCaseIds
      if (isCrossSetSelection && !selectedCaseIds?.length) {
        return null
      }

      // Use shared seed if provided, otherwise generate a new one
      const subSampleSeed = templateData.shouldSubSample
        ? (sharedSeed ?? Math.floor(Math.random() * 1000000))
        : undefined

      // Build display name with auto-suffix for multi-run scenarios
      const displayName = buildDisplayName(agentId, personaId, testSetId)

      const v1Request: LaunchRunV1Request = {
        agent_id: agentId,
        persona_id: personaId,
        test_set_id: selectedTestSet.id,
        metric_ids:
          metricIds.length > 0 || templateData.hasAutomaticMetrics
            ? metricIds
            : undefined,
        mutation_ids:
          mutationIds && mutationIds.length > 0 ? mutationIds : undefined,
        options: {
          iteration_count: templateData.iterationCount || 1,
          concurrency: templateData.concurrency || 1,
          sub_sample_size: templateData.shouldSubSample
            ? templateData.subSampleSize || 1
            : undefined,
          sub_sample_seed: subSampleSeed,
          test_case_ids: selectedCaseIds,
        },
        metadata: {
          ...(customerMetadata ? { customer: customerMetadata } : {}),
          created_by: username,
          template_name: templateData.name,
          display_name: displayName,
          tags: tags && tags.length > 0 ? tags : undefined,
        },
        config_overrides: {
          test_case_params_by_iteration: templateData.testCaseParamsByIteration,
        },
      }

      return v1Request
    },
    [
      templateData,
      selectedTestCaseIdsByTestSet,
      testSetRecords,
      metricRecords,
      username,
      buildDisplayName,
      tags,
      customerMetadata,
    ],
  )

  return {
    generateLaunchRunRequestForAgent,
    isLaunchDisabled,
  }
}
